import { useEffect, useMemo, useState } from "react";
import { Calendar, Captions as CaptionsIcon, ClipboardList, Clock, Download, History as HistoryIcon, Loader2, Play, Video, X } from "lucide-react";
import { useMeetings } from "@/api/hooks";
import { useAccessToken } from "@/auth/useAccessToken";
import { connectParityApi, type Meeting, type RecordingSummary, type TranscriptLine } from "@/api/connectApi";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { RecordingCard, useMeetingRecordings } from "./recording/MeetingRecordings";
import { MeetingRecapDialog } from "./meeting/MeetingRecap";

/**
 * Teams-style call history. Lists past meetings (status === "Ended"
 * or scheduled end already passed) with computed duration plus any
 * recordings the user has access to: recordings saved to the host's Drive
 * (Open / Download / Open in Drive, with each viewer's own Drive access)
 * and older ones Connect stored itself, which stream the .webm out of
 * MinIO via the BFF.
 */
export function CallHistoryPage() {
    const meetings = useMeetings();

    const past = useMemo<Meeting[]>(() => {
        const all = meetings.data ?? [];
        const now = Date.now();
        return all
            .filter((m) => {
                if (m.status === "Ended" || m.status === "Cancelled") return true;
                if (m.status === "Live") return false;
                // Ad-hoc calls carry no scheduledEnd, and meetings no longer
                // flip to Ended the moment the room empties (that auto-end was
                // far too eager and made links unusable). Without assuming a
                // duration here, a call you finished five minutes ago never
                // appeared in history at all.
                const end = m.scheduledEnd
                    ? new Date(m.scheduledEnd).getTime()
                    : new Date(m.actualEnd ?? m.scheduledStart).getTime() + 60 * 60_000;
                return end < now;
            })
            .sort((a, b) => {
                const at = (a.actualEnd ?? a.scheduledEnd ?? a.scheduledStart);
                const bt = (b.actualEnd ?? b.scheduledEnd ?? b.scheduledStart);
                return new Date(bt).getTime() - new Date(at).getTime();
            });
    }, [meetings.data]);

    return (
        <main className="min-h-0 flex-1 overflow-auto bg-background">
            <div className="mx-auto max-w-3xl p-6">
                <header className="mb-4 flex items-center gap-2">
                    <HistoryIcon className="h-5 w-5 text-primary" aria-hidden />
                    <h1 className="text-lg font-semibold">Call history</h1>
                    <span className="ml-auto text-xs text-muted-foreground">
                        {past.length} meeting{past.length === 1 ? "" : "s"}
                    </span>
                </header>

                {meetings.isPending ? (
                    <p className="text-sm text-muted-foreground">Loading…</p>
                ) : past.length === 0 ? (
                    <div className="rounded-lg border bg-card p-10 text-center text-sm text-muted-foreground">
                        <Video className="mx-auto mb-2 h-6 w-6" aria-hidden />
                        <p>No past meetings yet.</p>
                        <p className="text-xs">Meetings you host or join will appear here once they end.</p>
                    </div>
                ) : (
                    <ul role="list" className="flex flex-col gap-2">
                        {past.map((m) => (
                            <CallHistoryRow key={m.id} meeting={m} />
                        ))}
                    </ul>
                )}
            </div>
        </main>
    );
}

function CallHistoryRow({ meeting }: { meeting: Meeting }) {
    const token = useAccessToken();
    const [open, setOpen] = useState(false);
    const [transcriptOpen, setTranscriptOpen] = useState(false);
    const [recapOpen, setRecapOpen] = useState(false);
    const recordings = useQuery({
        queryKey: ["recordings", meeting.id, token],
        queryFn: () => connectParityApi.listRecordings(meeting.id, token),
        enabled: open && Boolean(token)
    });
    // Drive recordings load only once the row is expanded, like the old ones.
    const driveRecordings = useMeetingRecordings(meeting.id, open);
    const drive = driveRecordings.data ?? [];
    const legacy = recordings.data ?? [];
    const loadingRecordings = recordings.isPending
        || (driveRecordings.isPending && driveRecordings.fetchStatus === "fetching");
    const transcript = useQuery({
        queryKey: ["transcript", meeting.id, token],
        queryFn: () => connectParityApi.listTranscript(meeting.id, token),
        enabled: open && transcriptOpen && Boolean(token)
    });

    const start = meeting.actualStart ?? meeting.scheduledStart;
    const end = meeting.actualEnd ?? meeting.scheduledEnd;
    const duration = computeDuration(start, end);

    return (
        <li className="rounded-lg border bg-card">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-accent"
                aria-expanded={open}
            >
                <Video className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                        <span className="truncate text-sm font-medium">{meeting.subject}</span>
                        <span
                            className={cn(
                                "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
                                meeting.status === "Ended" ? "bg-muted text-muted-foreground" :
                                meeting.status === "Cancelled" ? "bg-destructive/10 text-destructive" :
                                "bg-amber-500/15 text-amber-600"
                            )}
                        >
                            {meeting.status}
                        </span>
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1">
                            <Calendar className="h-3 w-3" /> {formatDateTime(start)}
                        </span>
                        {duration ? (
                            <span className="inline-flex items-center gap-1">
                                <Clock className="h-3 w-3" /> {duration}
                            </span>
                        ) : null}
                    </div>
                </div>
            </button>

            {open ? (
                <div className="border-t px-4 py-3 text-xs">
                    {loadingRecordings && drive.length === 0 && legacy.length === 0 ? (
                        <p className="flex items-center gap-2 text-muted-foreground">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading recordings…
                        </p>
                    ) : drive.length === 0 && legacy.length === 0 ? (
                        <p className="text-muted-foreground">No recordings for this meeting.</p>
                    ) : (
                        <div className="flex flex-col gap-1.5">
                            {drive.length > 0 ? (
                                <section aria-label="Recordings in Drive" className="flex flex-col gap-1.5">
                                    {drive.map((r) => <RecordingCard key={r.id} recording={r} className="max-w-none" />)}
                                </section>
                            ) : null}
                            {legacy.length > 0 ? (
                                <ul className="flex flex-col gap-1.5" aria-label="Recordings stored in Connect">
                                    {legacy.map((r) => (
                                        <RecordingRow key={r.id} recording={r} />
                                    ))}
                                </ul>
                            ) : null}
                        </div>
                    )}

                    <div className="mt-3 flex flex-wrap gap-1.5">
                        {meeting.status !== "Cancelled" ? (
                            <button
                                type="button"
                                onClick={() => setRecapOpen(true)}
                                className="inline-flex items-center gap-1 rounded-md border border-primary/40 px-2 py-1 font-medium text-primary hover:bg-primary/10"
                                title="Attendance, duration, recordings, files and chat"
                            >
                                <ClipboardList className="h-3.5 w-3.5" />
                                Recap
                            </button>
                        ) : null}
                        <button
                            type="button"
                            onClick={() => setTranscriptOpen((v) => !v)}
                            className="inline-flex items-center gap-1 rounded-md border px-2 py-1 hover:bg-accent"
                            aria-expanded={transcriptOpen}
                        >
                            <CaptionsIcon className="h-3.5 w-3.5" />
                            {transcriptOpen ? "Hide transcript" : "Show transcript"}
                        </button>
                        <DownloadIcsButton meetingId={meeting.id} subject={meeting.subject} />
                    </div>

                    {transcriptOpen ? (
                        <div className="mt-2 max-h-72 overflow-auto rounded-md border bg-background p-2">
                            {transcript.isPending ? (
                                <p className="flex items-center gap-2 text-muted-foreground">
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading transcript…
                                </p>
                            ) : !transcript.data || transcript.data.length === 0 ? (
                                <p className="text-muted-foreground">No transcript captured for this meeting.</p>
                            ) : (
                                <ul className="flex flex-col gap-1">
                                    {transcript.data.map((line) => (
                                        <TranscriptRow key={line.id} line={line} />
                                    ))}
                                </ul>
                            )}
                        </div>
                    ) : null}
                </div>
            ) : null}
            {recapOpen ? <MeetingRecapDialog meetingId={meeting.id} onClose={() => setRecapOpen(false)} /> : null}
        </li>
    );
}

/**
 * Downloads the meeting as a .ics file via the authenticated
 * /api/meetings/{id}/ics endpoint. Goes the blob → object-URL route
 * because <a href="..."> wouldn't carry the bearer token, and the
 * endpoint requires authorization.
 */
function DownloadIcsButton({ meetingId, subject }: { meetingId: string; subject: string }) {
    const token = useAccessToken();
    const [busy, setBusy] = useState(false);

    const download = async () => {
        setBusy(true);
        try {
            const url = connectParityApi.meetingIcsUrl(meetingId);
            const res = await fetch(url, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                credentials: "omit"
            });
            if (!res.ok) return;
            const blob = await res.blob();
            const objectUrl = URL.createObjectURL(blob);
            try {
                const a = document.createElement("a");
                a.href = objectUrl;
                a.download = safeIcsName(subject);
                document.body.appendChild(a);
                a.click();
                a.remove();
            } finally {
                URL.revokeObjectURL(objectUrl);
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <button
            type="button"
            onClick={() => void download()}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded-md border px-2 py-1 hover:bg-accent disabled:opacity-50"
            title="Add this meeting to your calendar app"
        >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Calendar className="h-3.5 w-3.5" />}
            Add to calendar
        </button>
    );
}

function safeIcsName(subject: string): string {
    const cleaned = (subject ?? "")
        .replace(/[^A-Za-z0-9_-]+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 60);
    return (cleaned || "meeting") + ".ics";
}

function TranscriptRow({ line }: { line: TranscriptLine }) {
    const t = new Date(line.spokenAt);
    const stamp = Number.isNaN(t.getTime())
        ? line.spokenAt
        : t.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", second: "2-digit" });
    return (
        <li className="text-xs">
            <span className="mr-1 font-mono text-muted-foreground">{stamp}</span>
            <span className="font-semibold">{line.speakerDisplayName || line.speakerUserId}:</span>{" "}
            <span>{line.text}</span>
        </li>
    );
}

function RecordingRow({ recording }: { recording: RecordingSummary }) {
    const token = useAccessToken();
    const [downloading, setDownloading] = useState(false);
    const [playUrl, setPlayUrl] = useState<string | null>(null);
    const [loadingPlay, setLoadingPlay] = useState(false);

    const fetchBlobUrl = async (): Promise<string | null> => {
        const url = connectParityApi.recordingDownloadUrl(recording.id);
        const res = await fetch(url, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            credentials: "omit"
        });
        if (!res.ok) return null;
        const blob = await res.blob();
        return URL.createObjectURL(blob);
    };

    const togglePlay = async () => {
        if (playUrl) {
            URL.revokeObjectURL(playUrl);
            setPlayUrl(null);
            return;
        }
        setLoadingPlay(true);
        try {
            const u = await fetchBlobUrl();
            if (u) setPlayUrl(u);
        } finally {
            setLoadingPlay(false);
        }
    };

    // Make sure the object URL is revoked if the row unmounts while
    // playback is open — otherwise the blob hangs around in memory.
    useEffect(() => () => { if (playUrl) URL.revokeObjectURL(playUrl); }, [playUrl]);

    const download = async () => {
        setDownloading(true);
        try {
            const url = connectParityApi.recordingDownloadUrl(recording.id);
            const res = await fetch(url, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                credentials: "omit"
            });
            if (!res.ok) return;
            const blob = await res.blob();
            const objectUrl = URL.createObjectURL(blob);
            try {
                const a = document.createElement("a");
                a.href = objectUrl;
                a.download = `recording-${recording.id}.webm`;
                document.body.appendChild(a);
                a.click();
                a.remove();
            } finally {
                URL.revokeObjectURL(objectUrl);
            }
        } finally {
            setDownloading(false);
        }
    };

    return (
        <li className="rounded-md border bg-background">
            <div className="flex items-center gap-2 px-3 py-2">
                <Video className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-foreground">
                        Recorded by {recording.capturedByDisplayName}
                    </div>
                    <div className="text-muted-foreground">
                        {formatDuration(recording.duration)} · {formatBytes(recording.sizeBytes)} · {formatDateTime(recording.createdAt)}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => void togglePlay()}
                    disabled={loadingPlay}
                    className="flex items-center gap-1 rounded-md border px-2 py-1 hover:bg-accent disabled:opacity-50"
                    title={playUrl ? "Hide player" : "Play in browser"}
                >
                    {loadingPlay
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : playUrl
                            ? <X className="h-3.5 w-3.5" />
                            : <Play className="h-3.5 w-3.5" />}
                    {playUrl ? "Hide" : "Play"}
                </button>
                <button
                    type="button"
                    onClick={() => void download()}
                    disabled={downloading}
                    className="flex items-center gap-1 rounded-md border px-2 py-1 hover:bg-accent disabled:opacity-50"
                    title="Download recording"
                >
                    {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                    Download
                </button>
            </div>
            {playUrl ? (
                <div className="border-t bg-black p-2">
                    {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                    <video
                        src={playUrl}
                        controls
                        autoPlay
                        className="aspect-video w-full max-h-96 rounded bg-black"
                    />
                </div>
            ) : null}
        </li>
    );
}

function computeDuration(startIso: string, endIso?: string | null): string | null {
    if (!endIso) return null;
    const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
    if (!Number.isFinite(ms) || ms <= 0) return null;
    const totalSeconds = Math.round(ms / 1000);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
}

/** Parse a .NET TimeSpan ("hh:mm:ss" or "d.hh:mm:ss.fff") into a friendly summary. */
function formatDuration(ts: string): string {
    const m = /^(?:(\d+)\.)?(\d{2}):(\d{2}):(\d{2})/.exec(ts);
    if (!m) return ts;
    const days = m[1] ? parseInt(m[1], 10) : 0;
    const hours = parseInt(m[2]!, 10) + days * 24;
    const mins = parseInt(m[3]!, 10);
    const secs = parseInt(m[4]!, 10);
    if (hours > 0) return `${hours}h ${mins}m`;
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
}

function formatDateTime(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
        month: "short", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit"
    });
}

function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(0)} KB`;
    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    return `${(mb / 1024).toFixed(2)} GB`;
}
